import { useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { EmptyState } from '@/design/components'
import { cn } from '@/lib/cn'
import { PDF_OPTIONS } from '../pdf'
import type { ExtractionField } from '../types'

export interface DocumentPreviewProps {
  /** Blob URL pliku (patrz `useDocumentFile`). */
  url: string | null
  /** Typ MIME pliku — decyduje o rendererze (pdf.js kontra <img>). */
  mime?: string | null
  error?: string | null
  /** Pola z bboxem — podświetlamy te z bieżącej strony. */
  fields: ExtractionField[]
  /**
   * Podświetlone pole (najechane/edytowane) — mocniejszy kolor. Identyfikujemy je
   * POZYCJĄ w `fields`, bo klucze potrafią się powtarzać (np. dwie `party`) i
   * porównanie po kluczu podświetlałoby obie ramki naraz.
   */
  activeIndex?: number | null
  ocrQuality?: number | null
  onDownload?: () => void
  fallbackPages?: number | null
}

/** Podgląd skanu (PDF albo obraz) z warstwą podświetleń pól rozpoznanych przez model. */
export function DocumentPreview({
  url,
  mime,
  error,
  fields,
  activeIndex,
  ocrQuality,
  onDownload,
  fallbackPages,
}: DocumentPreviewProps) {
  // Skrzynka przyjmuje też PNG/JPG (zdjęcie pisma, skaner bez PDF-a) — obraz musi
  // iść do <img>, bo pdf.js wywala się na nim „Invalid PDF structure".
  const isImage = Boolean(mime?.startsWith('image/'))
  const isPdf = !mime || mime === 'application/pdf'

  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(560)
  const [pageCount, setPageCount] = useState<number>(fallbackPages ?? 1)
  const [pageNumber, setPageNumber] = useState(1)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (isImage) {
      setPageCount(1)
      setPageNumber(1)
    }
  }, [isImage])

  useEffect(() => {
    const element = containerRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width
      if (next && next > 0) setWidth(Math.min(next, 760))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Zachowujemy oryginalny indeks pola — to on jest tożsamością wiersza.
  const highlights = useMemo(
    () =>
      fields
        .map((field, index) => ({ field, index }))
        .filter(({ field }) => field.bbox?.length === 4 && field.page === pageNumber),
    [fields, pageNumber],
  )

  const quality =
    ocrQuality === null || ocrQuality === undefined
      ? null
      : `${(ocrQuality * 100).toFixed(1).replace('.', ',')}%`

  return (
    <section className="am-card flex min-w-0 flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-2.5">
        <div className="flex items-center gap-2">
          <PagerButton
            label="Poprzednia strona"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
          >
            ‹
          </PagerButton>
          <span className="t-mono text-ink">
            {pageNumber} / {pageCount}
          </span>
          <PagerButton
            label="Następna strona"
            disabled={pageNumber >= pageCount}
            onClick={() => setPageNumber((current) => Math.min(pageCount, current + 1))}
          >
            ›
          </PagerButton>
        </div>
        <div className="flex items-center gap-3 text-micro text-muted">
          <span>
            Warstwa OCR:{' '}
            {quality ? (
              <span className="font-medium text-success">rozpoznana ({quality})</span>
            ) : (
              <span className="font-medium text-danger">brak warstwy tekstowej</span>
            )}
          </span>
          {onDownload ? (
            <button
              type="button"
              onClick={onDownload}
              className="text-micro text-muted underline-offset-2 transition-colors hover:text-ink hover:underline"
            >
              Pobierz
            </button>
          ) : null}
        </div>
      </header>

      <div ref={containerRef} className="flex min-h-[420px] justify-center bg-[#eef1f4] p-5">
        {error || loadError ? (
          <EmptyState title="Nie udało się wczytać podglądu" description={error ?? loadError} />
        ) : !url ? (
          <PreviewSkeleton />
        ) : isImage ? (
          <div className="relative inline-block bg-card shadow-card">
            <img
              src={url}
              alt="Podgląd skanu"
              style={{ width: width - 40 }}
              className="block h-auto"
              onError={() => setLoadError('Nie udało się wyświetlić obrazu.')}
            />
            <HighlightLayer highlights={highlights} activeIndex={activeIndex} />
          </div>
        ) : !isPdf ? (
          <EmptyState
            title="Podgląd niedostępny dla tego formatu"
            description="Plik możesz pobrać i otworzyć lokalnie."
          />
        ) : (
          <Document
            file={url}
            options={PDF_OPTIONS}
            loading={<PreviewSkeleton />}
            error={<EmptyState title="Nie udało się wczytać pliku PDF" />}
            onLoadSuccess={({ numPages }) => {
              setPageCount(numPages)
              setPageNumber((current) => Math.min(current, numPages))
            }}
            onLoadError={(err: Error) => setLoadError(err.message)}
          >
            <div className="relative inline-block bg-card shadow-card">
              <Page
                pageNumber={pageNumber}
                width={width - 40}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={<PreviewSkeleton />}
              />
              <HighlightLayer highlights={highlights} activeIndex={activeIndex} />
            </div>
          </Document>
        )}
      </div>

      <footer className="border-t border-line-soft px-4 py-2 text-center text-micro text-muted">
        podgląd skanu · str. {pageNumber}
      </footer>
    </section>
  )
}

/** Ramki pól z bboxem — wspólne dla podglądu PDF i obrazu. */
function HighlightLayer({
  highlights,
  activeIndex,
}: {
  highlights: { field: ExtractionField; index: number }[]
  activeIndex?: number | null
}) {
  return (
    <div className="pointer-events-none absolute inset-0">
      {highlights.map(({ field, index }) => {
        const [x0, y0, x1, y1] = field.bbox as number[]
        const active = activeIndex === index
        return (
          <span
            key={`${field.key}-${index}`}
            title={`${field.label}: ${field.value}`}
            className={cn(
              'absolute rounded-[1px] border',
              active
                ? 'border-gold bg-[rgba(201,162,39,0.34)]'
                : 'border-[#e0c46a] bg-[rgba(201,162,39,0.16)]',
            )}
            style={{
              left: `${x0 * 100}%`,
              top: `${y0 * 100}%`,
              width: `${Math.max(x1 - x0, 0.01) * 100}%`,
              height: `${Math.max(y1 - y0, 0.008) * 100}%`,
            }}
          />
        )
      })}
    </div>
  )
}

function PagerButton({
  children,
  disabled,
  onClick,
  label,
}: {
  children: string
  disabled: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="h-[24px] w-[24px] rounded border border-line bg-card text-ink transition-colors duration-fast hover:bg-rowhover disabled:opacity-40"
    >
      {children}
    </button>
  )
}

function PreviewSkeleton() {
  return <div className="h-[520px] w-[380px] animate-pulse rounded bg-[#e3e8ec]" />
}
